import React, { useState } from "react";
import { AnswersType, FlagsType, ParticipantExamQuestion } from "../../../types/question";
import { Sidebar } from "./Sidebar/index";
import { CompletedScreen } from "./Completed";
import { useSaveAnswers } from "../../../utils/hooks/useSaveAnswers";
import { OnBlurWindowNotification } from "../../molecules/security/OnBlurWindowNotification";
import { usePreventCopyPaste } from "../../../utils/hooks/usePreventCopyPaste";
import { PageWrapper } from "../../atom/PageWrapper";
import { Summary } from "./Summary/index";
import { Questions } from "./Questions";
import { ParticipantExam, SummaryStateType } from "../../../types/components";
import { useUserContext } from "../../../utils/contexts/UserProvider";
import { useLocation } from "react-router-dom";
import { usePutResource, useResource } from "../../../services";
import { TimerProvider } from "../../../utils/contexts/TimerContext";
import { OMSpinner } from "../../atom/Spinner";
import { useExamCheck } from "../../../utils/hooks/useExamCheck";
import { createResourceUrl } from "../../../utils/data";
import { CalculatingResults } from "./Summary/CalculatingResults";

export const Journey = () => {
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [answers, setAnswers] = useState<AnswersType>({});
  const [flags, setFlags] = useState<FlagsType>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [flagHoverCount, setFlagHoverCount] = useState(0);
  const [isSummary, setIsSummary] = useState<SummaryStateType>({ state: false, filter: null });
  const { user } = useUserContext();
  const { state } = useLocation();
  const examId = state?.examId;
  const id = state?.id;
  const secondsLeft = state?.secondsLeft as number;
  const isPractice: boolean = !!state?.isPractice;

  const {
    data: examQuestions,
    isLoading: isQuestionsLoading,
    mutate: mutateQuestions,
  } = useResource<ParticipantExamQuestion[]>(
    createResourceUrl("/common/participant-exam-question", {
      include: [
        "participant_exam",
        "participant_exam.exam",
        "selected_answer",
        "exam_question",
        "exam_question.exam_answers",
      ],
      filters: {
        "eq:participant_id": user?.id,
        "eq:participant_exam_id": examId,
      },
      per_page: 999,
    }),
    user && examId,
  );
  const questions = examQuestions ?? [];
  const { updateResource } = usePutResource(`/common/participant-exam-question`);
  const [resultExam, setResultExam] = useState<ParticipantExam | null>(null);
  const timeLeft = secondsLeft ?? 0;
  useSaveAnswers(questions, setAnswers, setFlags);
  const { isExamLoading, blurCount, setBlurCount } = useExamCheck(examId, isPractice);
  usePreventCopyPaste();

  const handleOptionChange = (answer: string | null) => {
    setAnswers((prev) => {
      const newData = { ...prev };

      if (answer === null) {
        delete newData[questions?.[currentQuestion]?.id];
      } else {
        newData[questions[currentQuestion]?.id] = answer;
      }

      return newData;
    });
  };

  const onSelectQuestion = (index: number) => {
    setCurrentQuestion(index);
  };

  const toggleFlag = async (questionNumber?: number) => {
    const questionId = questions?.[questionNumber ?? currentQuestion]?.id;

    if (!questionId) {
      return;
    }

    setFlags((prev) => {
      const newState = !prev[questionId];

      const newData = {
        ...prev,
        [questionId]: newState,
      };

      updateResource({
        resourceId: questionId,
        is_flagged: newState,
      });

      return newData;
    });
  };

  if (isQuestionsLoading || isExamLoading) {
    return <OMSpinner />;
  }

  if (isCompleted && resultExam?.percentage == undefined) {
    return (
      <CalculatingResults
        setResultExam={setResultExam}
        participantExamId={examId}
        mutateQuestions={mutateQuestions}
      />
    );
  }

  if (isCompleted) {
    return (
      <CompletedScreen
        questions={questions}
        answers={answers}
        examId={examId}
        resultExam={resultExam}
        isPractice={isPractice}
      />
    );
  }

  if (isSummary?.state) {
    return (
      <TimerProvider initialTime={timeLeft}>
        <Summary
          id={examId}
          examId={id}
          questions={questions}
          answers={answers}
          setIsSummary={setIsSummary}
          flags={flags}
          setIsCompleted={setIsCompleted}
          isSummary={isSummary}
          setCurrentQuestion={setCurrentQuestion}
          isPractice={isPractice}
        />
        {!isPractice && (
          <OnBlurWindowNotification
            setIsCompleted={setIsCompleted}
            blurCount={blurCount}
            setBlurCount={setBlurCount}
          />
        )}
      </TimerProvider>
    );
  }

  return (
    <TimerProvider initialTime={timeLeft}>
      <PageWrapper
        children2={
          <>
            <Sidebar
              questions={questions}
              currentQuestion={currentQuestion}
              answers={answers}
              flags={flags}
              onSelectQuestion={onSelectQuestion}
              toggleFlag={toggleFlag}
              setIsSummary={setIsSummary}
              setIsCompleted={setIsCompleted}
              isPractice={isPractice}
            />
            {!isPractice && (
              <OnBlurWindowNotification
                setIsCompleted={setIsCompleted}
                blurCount={blurCount}
                setBlurCount={setBlurCount}
              />
            )}
          </>
        }>
        <Questions
          currentQuestion={currentQuestion}
          questions={questions}
          answers={answers}
          setCurrentQuestion={setCurrentQuestion}
          toggleFlag={toggleFlag}
          flags={flags}
          handleOptionChange={handleOptionChange}
          flagHoverCount={flagHoverCount}
          setFlagHoverCount={setFlagHoverCount}
          setIsSummary={setIsSummary}
        />
      </PageWrapper>
    </TimerProvider>
  );
};
